import React, { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import moment from "moment";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import styles from "./style.module.css";
import { OTP_TRIGGER, timer, track, trackError } from "../../../analytics";
import { getOTP, verifyOtpAndAction, verifyOtpActionForDelivery, verifyOtpActionForEdit } from "../../../utils/api";
import { getAWBSuccess } from "../../../store/actions/awb";
import { setCompanyInfo } from "../../../store/actions/companyInfo";
import { saveOrderDetails } from "../../../utils/manageOrderStore";
import { saveOtpSession } from "../../../utils/otpSession";

// The on-screen keyboard does not move position:fixed elements: the browser shrinks
// (and on iOS scrolls) the visual viewport instead, so a bottom sheet ends up behind the
// keyboard. This follows the visual viewport and returns its box while the keyboard is
// open (null when it is closed or the API is missing), so the sheet can sit just above it.
const KEYBOARD_MIN = 120; // px of hidden height that counts as a keyboard, not a toolbar change

const useKeyboardViewport = () => {
  const [box, setBox] = useState(null);
  useEffect(() => {
    const vv = typeof window !== "undefined" ? window.visualViewport : null;
    if (!vv) return undefined;
    let frame = 0;
    const update = () => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(() => {
        const hidden = window.innerHeight - vv.height;
        setBox(hidden > KEYBOARD_MIN ? { top: Math.round(vv.offsetTop), height: Math.round(vv.height) } : null);
      });
    };
    update();
    vv.addEventListener("resize", update);
    vv.addEventListener("scroll", update);
    return () => {
      cancelAnimationFrame(frame);
      vv.removeEventListener("resize", update);
      vv.removeEventListener("scroll", update);
    };
  }, []);
  return box;
};

// OTP Verification modal from the Claude Design file "OTP & Package Modals.dc.html".
// Renders its own overlay and card (a bottom sheet below 560px). The verify branches
// per `action` are unchanged.

const LENGTH = 6;
const RESEND_SECONDS = 30;
const EMPTY = Array(LENGTH).fill("");

// Show "+91 ••••• •8674" only for a real number; public responses carry an obfuscated value.
const maskPhone = (value) => {
  const digits = String(value || "").replace(/\D/g, "");
  if (digits.length < 10 || digits.length > 12 || /[a-z]/i.test(String(value))) return "";
  return `+91 ••••• •${digits.slice(-4)}`;
};

const Otp = ({
  reschedule,
  activeStatus,
  awb,
  setDelivery,
  orderInfo,
  subOrderId,
  setotpPage,
  action,
  setDeliveryOtp,
  setItemListModal,
  setActivestatus,
  status,
  setsuccessmessagemodal,
  setcancelmessagemodal,
  close,
}) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const trackdata = useSelector((store) => store?.awbReducer?.data);
  const phone = maskPhone(trackdata?.orderDetail?.[0]?.order?.shipping_msisdn);

  const [code, setCode] = useState(EMPTY);
  const [focus, setFocus] = useState(0);
  const keyboard = useKeyboardViewport();
  const sheetRef = useRef(null);
  const verifyRef = useRef(null);

  // With the keyboard open, keep the Verify button in view inside the sheet.
  useEffect(() => {
    if (!keyboard) return;
    const sheet = sheetRef.current;
    const button = verifyRef.current;
    if (!sheet || !button) return;
    const overflow = button.getBoundingClientRect().bottom - sheet.getBoundingClientRect().bottom;
    if (overflow > 0) sheet.scrollTop += overflow + 12;
  }, [keyboard]);
  const [error, setError] = useState("");
  const [shake, setShake] = useState(0);
  const [loading, setLoading] = useState(false);
  const [left, setLeft] = useState(RESEND_SECONDS);
  const refs = useRef([]);
  const OTP = code.join("");
  const full = OTP.length === LENGTH;

  // Funnel: one tp_otp_result per check; tp_otp_abandoned when the modal closes without one succeeding.
  const trigger = OTP_TRIGGER[action] || "manage_submit";
  const stats = useRef({ attempts: 0, resends: 0, last: "", done: false, elapsed: timer() });
  const report = (data) => {
    const s = stats.current;
    s.attempts += 1;
    s.last =
      data?.status === "Success" ? "success"
      : data === null || data === undefined ? "network"
      : data?.code === "429" ? "rate_limited"
      : data?.code === "500" || /invalid/i.test(String(data?.status || data?.message || "")) ? "invalid"
      : "other";
    if (s.last === "success") s.done = true;
    track("tp_otp_result", { result: s.last, attempt_no: s.attempts, elapsed_ms: s.elapsed(), resend_count: s.resends, trigger });
  };
  useEffect(
    () => () => {
      const s = stats.current;
      if (!s.done) track("tp_otp_abandoned", { attempts: s.attempts, resend_count: s.resends, elapsed_ms: s.elapsed(), last_result: s.last || "none", trigger });
    },
    // Reports once, when the modal unmounts.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  // Callers pass inline functions; keep the latest in a ref so the effects below run once.
  const closeRef = useRef(null);
  closeRef.current = () => (close ? close() : setotpPage(false));
  const onClose = useCallback(() => closeRef.current(), []);

  // Escape closes; page scroll is locked while open.
  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && onClose();
    const overflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    document.addEventListener("keydown", onKey);
    const t = setTimeout(() => refs.current[0]?.focus(), 400);
    return () => {
      document.body.style.overflow = overflow;
      document.removeEventListener("keydown", onKey);
      clearTimeout(t);
    };
  }, [onClose]);

  // Resend countdown.
  useEffect(() => {
    if (left <= 0) return undefined;
    const t = setTimeout(() => setLeft((s) => s - 1), 1000);
    return () => clearTimeout(t);
  }, [left]);

  const put = (next, focusIdx) => {
    setCode(next);
    setError("");
    if (focusIdx != null) refs.current[focusIdx]?.focus();
  };

  const fail = (data) => {
    setError(data?.code === "429" ? "Too many attempts. Please wait a few minutes and try again." : "Incorrect OTP. Please check and try again.");
    setShake((n) => n + 1);
    setTimeout(() => refs.current[LENGTH - 1]?.focus(), 0);
  };

  // The full-details response already has the customer details Manage Order needs. Keep it
  // (store + per-AWB copy) so that page opens complete, instead of rendering an empty form
  // and asking for the same details again.
  const keepOrderDetails = (data) => {
    if (!data?.result) return;
    dispatch(setCompanyInfo(data.result.companyInfo));
    dispatch(getAWBSuccess("data", data.result));
    saveOrderDetails(awb, data.result);
  };

  const vertifycode = async () => {
    if (action === "Edit") {
      const data = await verifyOtpActionForEdit(OTP, awb, setotpPage);
      report(data);
      if (data?.status === "Success") {
        saveOtpSession(awb, OTP);
        keepOrderDetails(data);
        navigate(`/${awb}/modifyorder`);
      } else fail(data);
    } else if (action === "Edit2") {
      sessionStorage.setItem("showdetails", true);
      const data = await verifyOtpActionForEdit(OTP, awb, setotpPage);
      report(data);
      if (data?.status === "Success") {
        saveOtpSession(awb, OTP);
        navigate(`/${awb}/showdetails`);
      } else fail(data);
    } else if (action === "cancel") {
      const data = await verifyOtpActionForEdit(OTP, awb, setotpPage);
      report(data);
      if (data?.status === "Success") {
        saveOtpSession(awb, OTP);
        keepOrderDetails(data);
        navigate(`/${awb}/modifyorder?isCancelling=true`);
      } else fail(data);
    } else if (action === "requestotp") {
      const data = await verifyOtpActionForDelivery(OTP, awb, setotpPage);
      report(data);
      if (data?.status === "Success") {
        sessionStorage.setItem("delivery_otp", data.result);
        track("tp_delivery_otp", { result: "shown", via: "modal" });
        setDeliveryOtp && setDeliveryOtp(data.result);
        setDelivery && setDelivery(true);
        setotpPage(false);
      } else fail(data);
    } else if (action === "showProduct") {
      const data = await verifyOtpActionForEdit(OTP, awb, setotpPage);
      report(data);
      if (data?.status === "Success") {
        saveOtpSession(awb, OTP);
        dispatch(getAWBSuccess("data", data?.result));
        // The items list is a modal on every screen size now (a bottom sheet on phones).
        setItemListModal && setItemListModal(true);
      } else fail(data);
    } else if (action === "resheduleTab") {
      const data = await verifyOtpActionForEdit(OTP, awb, setotpPage, true);
      report(data);
      if (data?.status === "Success") {
        saveOtpSession(awb, OTP);
        dispatch(getAWBSuccess("data", data?.result));
        window.location.reload(true);
        if (status === "reschedule") setActivestatus({ cancel: false, [status]: true });
        else setActivestatus({ reschedule: false, [status]: true });
      } else fail(data);
    } else {
      const data = await verifyOtpAndAction(reschedule, OTP, activeStatus, awb, orderInfo, moment, subOrderId, setotpPage);
      report(data);
      if (data?.status === "Success") {
        saveOtpSession(awb, OTP);
        dispatch(getAWBSuccess("data", data?.result));
        setcancelmessagemodal && setcancelmessagemodal(true);
      } else fail(data);
    }
  };

  const verify = async () => {
    if (!full || loading) return;
    setLoading(true);
    try {
      await vertifycode();
    } catch (e) {
      report(undefined);
      fail();
    } finally {
      setLoading(false);
    }
  };

  const resend = async () => {
    put(EMPTY, 0);
    setLeft(RESEND_SECONDS);
    stats.current.resends += 1;
    const data = await getOTP(awb);
    if (data?.status !== "Success") {
      trackError("otp_send", data, { trigger, resend: 1 });
      setError("We couldn't resend the OTP. Please try again.");
    }
  };

  const onChange = (i) => (e) => {
    const d = e.target.value.replace(/\D/g, "");
    const next = [...code];
    if (d.length > 1) {
      // SMS autofill or typing over a filled box: spread the digits forward.
      d.slice(0, LENGTH - i)
        .split("")
        .forEach((c, k) => {
          next[i + k] = c;
        });
      return put(next, Math.min(LENGTH - 1, i + d.length));
    }
    next[i] = d;
    return put(next, d && i < LENGTH - 1 ? i + 1 : null);
  };

  const onKeyDown = (i) => (e) => {
    if (e.key === "Backspace" && !code[i] && i > 0) {
      e.preventDefault();
      const next = [...code];
      next[i - 1] = "";
      put(next, i - 1);
    } else if (e.key === "ArrowLeft" && i > 0) refs.current[i - 1]?.focus();
    else if (e.key === "ArrowRight" && i < LENGTH - 1) refs.current[i + 1]?.focus();
    else if (e.key === "Enter") verify();
  };

  const onPaste = (e) => {
    const d = (e.clipboardData.getData("text") || "").replace(/\D/g, "").slice(0, LENGTH);
    if (!d) return;
    e.preventDefault();
    const next = [...EMPTY];
    d.split("").forEach((c, k) => {
      next[k] = c;
    });
    put(next, Math.min(LENGTH - 1, d.length));
  };

  return createPortal(
    <div
      className={`${styles.wrap} ${keyboard ? styles.keyboardOpen : ""}`}
      style={keyboard ? { top: keyboard.top, height: keyboard.height, bottom: "auto" } : undefined}
    >
      <div className={styles.overlay} onClick={onClose} />
      <div ref={sheetRef} role="dialog" aria-modal="true" aria-label="OTP Verification" className={styles.sheet}>
        <span className={styles.grab} />
        <button type="button" aria-label="Close" className={styles.close} onClick={onClose}>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" aria-hidden="true">
            <path d="M6 6l12 12M18 6L6 18" />
          </svg>
        </button>

        <div className={styles.body}>
          <span className={styles.badge}>
            <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="#7130F0" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <path d="M12 3l7 3v5c0 4.5-3 8.3-7 10-4-1.7-7-5.5-7-10V6z" />
              <path d="M9 12l2 2 4-4" />
            </svg>
          </span>
          <div className={styles.copy}>
            <h2 className={styles.title}>OTP Verification</h2>
            <p className={styles.text}>
              To safe guard your personal information, please verify yourself as the buyer by entering verification code sent on registered mobile number
            </p>
            {phone && (
              <span className={styles.phone}>
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true">
                  <rect x="6" y="2" width="12" height="20" rx="3" />
                  <path d="M11 18h2" />
                </svg>
                {phone}
              </span>
            )}
          </div>

          <div className={styles.codeBlock}>
            <div key={shake} className={`${styles.boxes} ${shake ? styles.shake : ""}`}>
              {code.map((val, i) => (
                <input
                  key={i}
                  ref={(el) => {
                    refs.current[i] = el;
                  }}
                  value={val}
                  onChange={onChange(i)}
                  onKeyDown={onKeyDown(i)}
                  onPaste={onPaste}
                  onFocus={(e) => {
                    setFocus(i);
                    e.target.select();
                  }}
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  maxLength={i === 0 ? LENGTH : 1}
                  aria-label={`Digit ${i + 1}`}
                  aria-invalid={!!error}
                  className={[styles.box, val && styles.boxFilled, focus === i && styles.boxFocus, error && styles.boxError].filter(Boolean).join(" ")}
                />
              ))}
            </div>
            {error && (
              <span className={styles.error} role="alert">
                {error}
              </span>
            )}
          </div>

          <div className={styles.resendRow}>
            <span>OTP not received?</span>
            {left <= 0 ? (
              <button type="button" className={styles.resend} onClick={resend}>
                Resend
              </button>
            ) : (
              <span className={styles.timer}>Resend in 00:{String(left).padStart(2, "0")}</span>
            )}
          </div>

          <button ref={verifyRef} type="button" className={`${styles.verify} ${full ? styles.verifyOn : ""}`} onClick={verify} disabled={!full || loading}>
            {loading && <span className={styles.spinner} />}
            {loading ? "Verifying…" : "Verify and Continue"}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default Otp;
